import React from "react";
import { Flex } from "@hubble/web";

import ContentfulText from "components/parts/ContentfulText";
import type { BaseComponentProps } from "utils/contentfulBaseComponent";

import { ContentfulThemeProvider } from "components/parts/ContentfulThemeProvider";

import { ContentfulButton } from "components/parts/ContentfulButton";
import { BaseComponent } from "design-components/baseComponent/BaseComponent";

import {
  InstitutionsCardGridBackground,
  CardGridContainer,
  StyledGrid,
  CardContainer,
  MobileCard,
  TabletCard,
  DesktopCard,
  CardContentWrapper,
  GroupedContentWrapper,
  StyledButton,
  GroupedContentItemWrapper,
  GroupedContentLargeItemWrapper
} from "./InstitutionsCardGrid.styled";

export const InstitutionsCardGrid = (props: BaseComponentProps) => {
  const { theme, styles, text, groupedContent, json } = props;

  return (
    <ContentfulThemeProvider theme={theme}>
      <InstitutionsCardGridBackground background={styles.background}>
        <CardGridContainer>
          <ContentfulText {...text?.[0]} />
          <StyledGrid {...styles}>
            {groupedContent.map((baseComponentObject) => (
              <CardWrapper {...baseComponentObject} json={json} />
            ))}
          </StyledGrid>
        </CardGridContainer>
      </InstitutionsCardGridBackground>
    </ContentfulThemeProvider>
  );
};

const CardWrapper = (props: BaseComponentProps) => {
  const { styles, theme, backgroundImage } = props;
  return (
    <CardContainer {...styles}>
      <React.Fragment>
        <MobileCard
          front={{
            component: <CardContent {...props} />,
            background: `${styles?.background?.mobile} url(${backgroundImage?.mobile?.url}) no-repeat center center / cover`,
            isInverted: Boolean(theme === "dark")
          }}
        />
        <TabletCard
          front={{
            component: <CardContent {...props} />,
            background: `${styles?.background?.tablet || styles?.background?.mobile} url(${
              backgroundImage?.tablet?.url || backgroundImage?.desktop?.url
            }) no-repeat center center / cover`,
            isInverted: Boolean(theme === "dark")
          }}
        />
        <DesktopCard
          front={{
            component: <CardContent {...props} />,
            background: `${
              styles?.background?.desktop ||
              styles?.background?.tablet ||
              styles?.background?.mobile
            } url(${
              backgroundImage?.desktop?.url ||
              backgroundImage?.tablet?.url ||
              backgroundImage?.mobile?.url
            }) no-repeat center center / cover`,
            isInverted: Boolean(theme === "dark")
          }}
        />
      </React.Fragment>
    </CardContainer>
  );
};

const CardContent = (props: BaseComponentProps) => {
  const { text, json, cta, groupedContent } = props;
  return (
    <CardContentWrapper>
      {json?.productCards ? (
        <Flex
          flexDirection="column"
          height="100%"
          justifyContent="flex-end"
          alignItems="center"
        >
          <ContentfulText {...text?.[0]} />
          <GroupedContentWrapper>
            {text.map(
              (textObject, index) =>
                index > 0 && (
                  <GroupedContentItemWrapper alignItems={"center"}>
                    <ContentfulText {...textObject} />
                  </GroupedContentItemWrapper>
                )
            )}
          </GroupedContentWrapper>
          <StyledButton {...cta?.[0]} />
        </Flex>
      ) : json?.largeProductCards ? (
        <Flex
          flexDirection="column"
          height="100%"
          justifyContent="flex-start"
          alignItems="center"
        >
          <ContentfulText {...text?.[0]} />
          <GroupedContentWrapper>
            {text.map(
              (textObject, index) =>
                index > 0 && (
                  <GroupedContentLargeItemWrapper>
                    <ContentfulText {...textObject} />
                  </GroupedContentLargeItemWrapper>
                )
            )}
          </GroupedContentWrapper>
          <StyledButton {...cta?.[0]} />
        </Flex>
      ) : (
        <Flex flexDirection="column" height="100%" justifyContent="flex-end">
          {text.map((textObject) => (
            <ContentfulText {...textObject} />
          ))}
          {cta.map((ctaObject) => (
            <ContentfulButton {...ctaObject} />
          ))}
          {groupedContent?.map((baseComponentObject) => (
            <BaseComponent {...baseComponentObject} />
          ))}
        </Flex>
      )}
    </CardContentWrapper>
  );
};
